import { create } from '@bufbuild/protobuf';
import { AccountSchema, type Account } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import {
  type RouteContextQuery,
  type RouteContextSelect,
  type RouteId,
  type RouteSearchSelect,
} from '@/router/route-hooks.types';
import {
  useRouteSearch as useAuthenticatedRouteSearch,
  useAuthenticatedUser,
  useSortedUserAccounts,
  useUserAccounts,
} from '@/routes/_authenticated.hooks';
import { isAccountOwner } from '@/utils/account-utils';

const ROUTE_ID = '/_authenticated/_dashboard/accounts/$accountId' satisfies RouteId;

export const accountIdUrlParam = z.guid().brand<'AccountId'>();

/** Represents a unique identifier for an account coming from the URL param: accountId */
export type AccountId = z.infer<typeof accountIdUrlParam>;

/**
 * Returns the current account ID from the URL params. AccountId represents a unique token for the current account in the account navigation UI.
 */
export const useAccountId = () => useParams({ from: ROUTE_ID, select: ({ accountId }) => accountId });

/**
 * Returns the current selected account ID, derived from the URL param or search params.
 */
export const useSelectedAccountId = (): AccountId | undefined => {
  const paramAccountId = useSafeAccountId();
  const searchAccountId = useAuthenticatedRouteSearch((s) => s.account_id as AccountId | undefined);

  return paramAccountId ?? searchAccountId;
};

export const useRouteContextQuery = <TSelected extends RouteContextQuery>(
  select: RouteContextSelect<typeof ROUTE_ID, TSelected>,
) => useRouteContext({ from: ROUTE_ID, select });

export const useRouteSearch = <TSelected, TId = typeof ROUTE_ID>(select: RouteSearchSelect<TId, TSelected>) =>
  useSearch({ from: ROUTE_ID, select });

/**
 * Returns the current account from the URL path params without throwing an error if not found.
 */
export const useSafeAccountId = () =>
  useParams({ from: ROUTE_ID, select: ({ accountId }) => accountId, shouldThrow: false });

/**
 * Returns the current account from the URL param, ensuring it is always available.
 */
export const useAccount = (): Account => {
  const accountId = useAccountId();
  const accounts = useUserAccounts();
  // If the account is not found in the user's accounts list, return a default empty account object to avoid errors (e.g.: deleted account).
  return accounts.find((account: Account) => account.id === accountId) ?? create(AccountSchema);
};

/**
 * Returns the current selected account, derived from the URL param or search params.
 * If no account is found, it returns the user's default account (see `useSortedUserAccounts`) or undefined if there are no accounts.
 */
export const useSelectedAccount = (): Account | undefined => {
  const accountId = useSelectedAccountId();
  const sortedAccounts = useSortedUserAccounts();

  return sortedAccounts.find((account) => account.id === accountId) ?? sortedAccounts.at(0);
};

/**
 * Returns whether the current account is owned by the authenticated user.
 */
export const useAccountIsOwner = (): boolean => {
  const user = useAuthenticatedUser();
  const account = useAccount();

  return isAccountOwner(account, user.email);
};
